import {
  AdminDollarInput2,
  AdminInputPlaceholder,
  AdminRadioInput,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  LocationVDriveEntity,
  Modal,
  P,
  UserRole,
  displayCentsInDollars,
  formatDate,
  lightTheme,
  updateVDriveOverdraftLimit,
  useAuth,
  useBusyAction,
  useVDrive,
} from '@vgrubs/components';
import { useCallback, useEffect, useState } from 'react';
import { RESERVE_BY_LEVEL } from './vDriveHelpers';

/** The backend refuses anything above $10,000. */
const MAX_OVERDRAFT_LIMIT = 1000000;

const TOOLTIP =
  "How far the vDrive balance may go negative when neither the owner's cards nor the location's vGrubs balance cover a charge. Deliveries keep running until it is used up; then new ones are refused and a bank payment is started. The reserve is not free credit: what is used is owed. Without a limit set here it follows the balance's level: $100 to start, $200 and $300 earned with vDrive volume and on-time payments, back to $100 after a pause for being negative or a returned bank payment.";

/** "(set manually)", "(level 1, since October 27, 2026)" or "(automatic)". */
const limitSource = (vDrive: LocationVDriveEntity) => {
  if (vDrive.customOverdraftLimit != null) return 'set manually';
  if (vDrive.reserveLevel == null) return 'automatic';
  const since = vDrive.reserveLevelChangedAt
    ? `, since ${formatDate(vDrive.reserveLevelChangedAt)}`
    : '';
  return `level ${vDrive.reserveLevel}${since}`;
};

type Props = {
  vDrive: LocationVDriveEntity;
};

/**
 * The balance's reserve limit (the API's `overdraftLimit` - the UI says
 * "reserve"), by level or set by hand, and the admin-only way to change it.
 * A limit set by hand is left alone by the daily level-up.
 *
 * Saved on its own rather than with the page's Save button: it has its own
 * route, only admins may call it (support sees the value, not the pencil), and
 * the page's Save sends everything else back and leaves.
 */
export const OverdraftLimitField = ({ vDrive }: Props) => {
  const role = useAuth((state) => state.user?.role);
  const canEdit = role === UserRole.admin;
  const [open, setOpen] = useState(false);

  const limit = vDrive.overdraftLimit ?? 0;

  return (
    <>
      <AdminInputPlaceholder
        label="Reserve Limit"
        tooltip={TOOLTIP}
        $textCenter
        value={`${displayCentsInDollars(limit)} (${limitSource(vDrive)})`}
        onClick={canEdit ? () => setOpen(true) : undefined}
      />
      <OverdraftLimitModal
        isOpen={open}
        onClose={() => setOpen(false)}
        vDrive={vDrive}
      />
    </>
  );
};

/** "level 2: $200.00", or the three levels when the level is unknown. */
const automaticLabel = (vDrive: LocationVDriveEntity) => {
  const level = vDrive.reserveLevel;
  if (level != null && RESERVE_BY_LEVEL[level] != null) {
    return `level ${level}: ${displayCentsInDollars(RESERVE_BY_LEVEL[level])}`;
  }
  return '$100 / $200 / $300 by level';
};

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  vDrive: LocationVDriveEntity;
};

const OverdraftLimitModal = ({ isOpen, onClose, vDrive }: ModalProps) => {
  const setLocationVDrive = useVDrive((state) => state.setLocationVDrive);
  const [useDefault, setUseDefault] = useState(true);
  const [amount, setAmount] = useState(0);
  const { busy, run } = useBusyAction();

  // Every opening starts from what is saved, not from an abandoned edit.
  useEffect(() => {
    if (!isOpen) return;
    setUseDefault(vDrive.customOverdraftLimit == null);
    setAmount(vDrive.customOverdraftLimit ?? vDrive.overdraftLimit ?? 0);
  }, [isOpen, vDrive.customOverdraftLimit, vDrive.overdraftLimit]);

  const handleAmountChange = useCallback((value: number) => {
    setAmount(value);
  }, []);

  const save = async () => {
    if (!useDefault && amount > MAX_OVERDRAFT_LIMIT) {
      return ErrorToast(
        `The limit can't be above ${displayCentsInDollars(MAX_OVERDRAFT_LIMIT)}`
      );
    }

    const ok = await run(async () => {
      const { data } = await updateVDriveOverdraftLimit(vDrive.location.id, {
        overdraftLimit: useDefault ? null : amount,
      });
      // Only the billing fields: the page keeps its own filtered copy of the
      // rest (providers without `unknown`, unsaved assignment edits).
      setLocationVDrive({
        ...vDrive,
        overdraftLimit: data.data.overdraftLimit,
        customOverdraftLimit: data.data.customOverdraftLimit,
        reserveLevel: data.data.reserveLevel,
        reserveLevelChangedAt: data.data.reserveLevelChangedAt,
        outstandingAmount: data.data.outstandingAmount,
        pendingBank: data.data.pendingBank,
      });
    }, 'Reserve limit saved.');

    if (ok) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header="Reserve Limit"
    >
      <P $mb="24" $center>
        How far this location&apos;s vDrive balance may go negative when neither
        the owner&apos;s cards nor the location&apos;s vGrubs balance cover a
        charge. At the limit new deliveries are refused and a bank payment is
        started. A limit set here stays until it is put back on automatic.
      </P>

      <Div $flex $column $gap="12px">
        <AdminRadioInput
          shadowDesign
          checked={useDefault}
          onClick={() => setUseDefault(true)}
          label={`Automatic (${automaticLabel(vDrive)})`}
        />
        <AdminRadioInput
          shadowDesign
          checked={!useDefault}
          onClick={() => setUseDefault(false)}
          label="Custom limit"
        />
      </Div>

      {!useDefault && (
        <Div $mt="20">
          <AdminDollarInput2
            label="Limit"
            value={amount}
            max={MAX_OVERDRAFT_LIMIT}
            onValueChange={handleAmountChange}
            $textCenter
          />
          <Disclaimer $mt="8" $mb="0" $center $color={lightTheme.lightGray}>
            $0.00 means the balance may not go below zero at all.
          </Disclaimer>
        </Div>
      )}

      <Button
        $selected
        $w="100%"
        $mt="24"
        $loading={busy}
        disabled={busy}
        onClick={save}
      >
        Save
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={onClose}>
        Cancel
      </Button>
    </Modal>
  );
};
